// Estoque — Sprint A.4
import { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { fetchAuthSession } from 'aws-amplify/auth';
import {
  Tabs, Table, Input, Select, Checkbox, Button, Card, Statistic, Tag, Modal,
  Form, InputNumber, Typography, Space, Row, Col, Empty, Alert, AutoComplete,
  App as AntApp, Flex, Radio,
} from 'antd';
import {
  AppstoreOutlined, DashboardOutlined, FileTextOutlined, SearchOutlined,
  PlusOutlined, EditOutlined, WarningOutlined, FireOutlined, DollarOutlined,
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { API_URL } from '../config';
import { fmtDataBr } from '../utils/dataFmt';

const { Title, Text } = Typography;

async function getToken(): Promise<string> {
  const session = await fetchAuthSession();
  return session.tokens?.idToken?.toString() ?? '';
}

interface Material {
  id: string;
  nome: string;
  categoria: string | null;
  descricao: string | null;
  unidade: string;
  custo_unitario: number | string;
  estoque_atual: number | string;
  estoque_minimo: number | string;
  fornecedor_padrao: string | null;
  observacoes: string | null;
  abaixo_minimo: boolean;
  valor_estoque: number | string;
}

interface Movimentacao {
  id: string;
  tipo: 'entrada' | 'saida' | 'ajuste';
  quantidade: number | string;
  custo_unitario: number | string | null;
  custo_total: number | string | null;
  motivo: string | null;
  data_movimentacao: string;
  observacoes: string | null;
  material_nome: string;
  material_unidade: string;
  contrato_numero: string | null;
  atividade_titulo: string | null;
  criado_por: string | null;
  criado_em: string;
}

interface Dashboard {
  kpis: {
    total_materiais: number | string;
    abaixo_minimo: number | string;
    zerados: number | string;
    valor_total_estoque: number | string;
  };
  abaixo_minimo: Material[];
  movimentacoes_recentes: Movimentacao[];
}

const fmtBRL = (v: any) => Number(v || 0).toLocaleString('pt-BR',
  { style: 'currency', currency: 'BRL', minimumFractionDigits: 2 });
const fmtNum = (v: any) => Number(v || 0).toLocaleString('pt-BR',
  { minimumFractionDigits: 0, maximumFractionDigits: 2 });

type Aba = 'inventario' | 'movimentacoes' | 'painel';

function EstoqueInner() {
  const [aba, setAba] = useState<Aba>('inventario');
  const [materiais, setMateriais] = useState<Material[]>([]);
  const [movimentacoes, setMovimentacoes] = useState<Movimentacao[]>([]);
  const [dashboard, setDashboard] = useState<Dashboard | null>(null);
  const [loading, setLoading] = useState(true);
  const [filtroCategoria, setFiltroCategoria] = useState('');
  const [busca, setBusca] = useState('');
  const [somenteBaixo, setSomenteBaixo] = useState(false);
  const [modalMaterial, setModalMaterial] = useState<Material | 'novo' | null>(null);
  const [modalMov, setModalMov] = useState<{ material: Material; tipo: 'entrada' | 'saida' | 'ajuste' } | null>(null);

  useEffect(() => { carregar(); /* eslint-disable-next-line */ }, [aba, filtroCategoria, busca, somenteBaixo]);

  async function carregar() {
    setLoading(true);
    try {
      const token = await getToken();
      if (aba === 'inventario') {
        const r = await axios.post(`${API_URL}/comercial/propostas-geradas`,
          { action: 'admin_listar_materiais',
            categoria: filtroCategoria || undefined,
            busca: busca || undefined,
            somente_baixo: somenteBaixo || undefined },
          { headers: { Authorization: token } });
        const d = r.data.body ? JSON.parse(r.data.body) : r.data;
        setMateriais(d.materiais || []);
      } else if (aba === 'painel') {
        const r = await axios.post(`${API_URL}/comercial/propostas-geradas`,
          { action: 'admin_dashboard_estoque' },
          { headers: { Authorization: token } });
        const d = r.data.body ? JSON.parse(r.data.body) : r.data;
        setDashboard(d);
      }
    } finally { setLoading(false); }
  }

  const categorias = useMemo(() => {
    const s = new Set<string>();
    materiais.forEach(m => { if (m.categoria) s.add(m.categoria); });
    return Array.from(s).sort();
  }, [materiais]);

  const cols: ColumnsType<Material> = [
    {
      title: 'Material', dataIndex: 'nome', key: 'nome',
      render: (_, m) => (
        <div>
          <Text strong style={{ fontSize: 12 }}>
            {m.abaixo_minimo && <WarningOutlined style={{ color: '#B07500', marginRight: 4 }} />}
            {m.nome}
          </Text>
          {m.descricao && (
            <div>
              <Text type="secondary" style={{ fontSize: 10 }}>
                {m.descricao.slice(0, 80)}{m.descricao.length > 80 ? '…' : ''}
              </Text>
            </div>
          )}
        </div>
      ),
    },
    {
      title: 'Categoria', dataIndex: 'categoria', key: 'cat', width: 120,
      render: (v) => v ? <Tag color="blue">{v}</Tag> : '—',
    },
    {
      title: 'Estoque', dataIndex: 'estoque_atual', key: 'eq', align: 'right', width: 110,
      render: (_, m) => (
        <Text strong style={{ color: m.abaixo_minimo ? '#B07500' : '#222', fontSize: 12 }}>
          {fmtNum(m.estoque_atual)} {m.unidade}
        </Text>
      ),
    },
    {
      title: 'Mínimo', dataIndex: 'estoque_minimo', key: 'em', align: 'right', width: 100,
      render: (_, m) => <Text type="secondary" style={{ fontSize: 11 }}>{fmtNum(m.estoque_minimo)} {m.unidade}</Text>,
    },
    {
      title: 'Custo unit.', dataIndex: 'custo_unitario', key: 'cu', align: 'right', width: 110,
      render: (v) => fmtBRL(v),
    },
    {
      title: 'Valor', dataIndex: 'valor_estoque', key: 've', align: 'right', width: 120,
      render: (v) => <Text strong>{fmtBRL(v)}</Text>,
    },
    {
      title: 'Ações', key: 'ac', width: 220, align: 'right',
      render: (_, m) => (
        <Space size={4}>
          <Button size="small" onClick={() => setModalMov({ material: m, tipo: 'entrada' })}
            style={{ color: '#0a7f4a', borderColor: '#0a7f4a' }}>+ Entrada</Button>
          <Button size="small" onClick={() => setModalMov({ material: m, tipo: 'saida' })}
            style={{ color: '#B07500', borderColor: '#B07500' }}>− Saída</Button>
          <Button size="small" icon={<EditOutlined />} onClick={() => setModalMaterial(m)} />
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: '16px 24px', maxWidth: 1400, margin: '0 auto' }}>
      <Flex align="center" gap={14} wrap="wrap" style={{ marginBottom: 14 }}>
        <Title level={4} style={{ margin: 0, color: '#020D7F' }}>📦 Estoque de consumíveis</Title>
        <Text type="secondary" style={{ fontSize: 12 }}>
          Marcos geodésicos, EPIs, uniformes, materiais de campo. A Pam alerta quando cai abaixo do mínimo.
        </Text>
      </Flex>

      <Tabs
        activeKey={aba}
        onChange={(k) => setAba(k as Aba)}
        size="small"
        items={[
          { key: 'inventario', label: <span><AppstoreOutlined /> Inventário</span> },
          { key: 'painel', label: <span><DashboardOutlined /> Painel</span> },
          { key: 'movimentacoes', label: <span><FileTextOutlined /> Movimentações</span> },
        ]}
      />

      {aba === 'inventario' && (
        <>
          <Flex align="center" gap={10} wrap="wrap" style={{ marginBottom: 14 }}>
            <Input
              value={busca} onChange={e => setBusca(e.target.value)}
              placeholder="Buscar por nome ou descrição…"
              prefix={<SearchOutlined style={{ color: '#aaa' }} />}
              allowClear
              style={{ maxWidth: 260 }}
            />
            <Select
              value={filtroCategoria || undefined}
              onChange={(v) => setFiltroCategoria(v || '')}
              placeholder="Todas as categorias"
              allowClear
              style={{ width: 200 }}
              options={categorias.map(c => ({ label: c, value: c }))}
            />
            <Checkbox checked={somenteBaixo} onChange={e => setSomenteBaixo(e.target.checked)}>
              <Text style={{ fontSize: 12 }}>Só estoque baixo</Text>
            </Checkbox>
            <Button
              type="primary" icon={<PlusOutlined />} style={{ marginLeft: 'auto' }}
              onClick={() => setModalMaterial('novo')}
            >
              Novo material
            </Button>
          </Flex>

          <Table
            rowKey="id"
            columns={cols}
            dataSource={materiais}
            loading={loading}
            size="small"
            pagination={{ pageSize: 50, showSizeChanger: true }}
            rowClassName={(r) => r.abaixo_minimo ? 'row-pendente' : ''}
            locale={{ emptyText: <Empty description="Nenhum material cadastrado. Cadastre marcos geodésicos, EPIs, uniformes e outros consumíveis." /> }}
          />
        </>
      )}

      {aba === 'painel' && (
        loading ? <Card loading style={{ marginTop: 14 }} />
        : !dashboard ? null
        : <PainelEstoque dashboard={dashboard} onClickMaterial={(m) => setModalMaterial(m)} />
      )}

      {aba === 'movimentacoes' && (
        <ListaMovimentacoes movimentacoes={movimentacoes} setMovimentacoes={setMovimentacoes} />
      )}

      {modalMaterial !== null && (
        <ModalMaterial
          material={modalMaterial === 'novo' ? null : modalMaterial}
          categorias={categorias}
          onFechar={() => setModalMaterial(null)}
          onSalvo={async () => { setModalMaterial(null); await carregar(); }} />
      )}

      {modalMov !== null && (
        <ModalMovimentacao
          material={modalMov.material} tipoInicial={modalMov.tipo}
          onFechar={() => setModalMov(null)}
          onSalvo={async () => { setModalMov(null); await carregar(); }} />
      )}
    </div>
  );
}

function PainelEstoque({ dashboard, onClickMaterial }:
    { dashboard: Dashboard; onClickMaterial: (m: Material) => void }) {
  const k = dashboard.kpis;
  return (
    <>
      <Row gutter={[10, 10]} style={{ marginBottom: 14, marginTop: 14 }}>
        <Col xs={12} md={6}>
          <Card size="small" style={{ borderLeft: '3px solid #0930D0' }}>
            <Statistic
              title={<Space size={4}><AppstoreOutlined /> Total de materiais</Space>}
              value={Number(k.total_materiais)}
              valueStyle={{ color: '#0930D0', fontSize: 20 }}
            />
          </Card>
        </Col>
        <Col xs={12} md={6}>
          <Card size="small" style={{ borderLeft: '3px solid ' + (Number(k.abaixo_minimo) > 0 ? '#B07500' : '#888') }}>
            <Statistic
              title={<Space size={4}><WarningOutlined /> Abaixo do mínimo</Space>}
              value={Number(k.abaixo_minimo)}
              valueStyle={{ color: Number(k.abaixo_minimo) > 0 ? '#B07500' : '#888', fontSize: 20 }}
            />
          </Card>
        </Col>
        <Col xs={12} md={6}>
          <Card size="small" style={{ borderLeft: '3px solid ' + (Number(k.zerados) > 0 ? '#B91626' : '#888') }}>
            <Statistic
              title={<Space size={4}><FireOutlined /> Zerados</Space>}
              value={Number(k.zerados)}
              valueStyle={{ color: Number(k.zerados) > 0 ? '#B91626' : '#888', fontSize: 20 }}
            />
          </Card>
        </Col>
        <Col xs={12} md={6}>
          <Card size="small" style={{ borderLeft: '3px solid #0a7f4a' }}>
            <Statistic
              title={<Space size={4}><DollarOutlined /> Valor em estoque</Space>}
              value={fmtBRL(k.valor_total_estoque)}
              valueStyle={{ color: '#0a7f4a', fontSize: 16 }}
            />
          </Card>
        </Col>
      </Row>

      <Row gutter={[14, 14]}>
        <Col xs={24} md={12}>
          <Card
            size="small"
            title={
              <Text strong style={{ fontSize: 12, color: '#B07500', textTransform: 'uppercase', letterSpacing: 0.5 }}>
                <WarningOutlined /> Abaixo do mínimo ({dashboard.abaixo_minimo.length})
              </Text>
            }
          >
            {dashboard.abaixo_minimo.length === 0 ? (
              <Alert type="success" message="Tudo em ordem — nenhum item abaixo do mínimo." showIcon />
            ) : (
              <Space direction="vertical" size={6} style={{ width: '100%' }}>
                {dashboard.abaixo_minimo.map(m => (
                  <Card
                    key={m.id} size="small" hoverable
                    onClick={() => onClickMaterial(m)}
                    style={{ background: '#FFF8E8', borderColor: '#F0D88E' }}
                    bodyStyle={{ padding: 8 }}
                  >
                    <Text strong style={{ fontSize: 12 }}>{m.nome}</Text>
                    <div>
                      <Text type="secondary" style={{ fontSize: 10 }}>
                        Tem {fmtNum(m.estoque_atual)} {m.unidade} ·
                        mínimo {fmtNum(m.estoque_minimo)} {m.unidade}
                        {m.fornecedor_padrao && <> · fornecedor: <strong>{m.fornecedor_padrao}</strong></>}
                      </Text>
                    </div>
                  </Card>
                ))}
              </Space>
            )}
          </Card>
        </Col>

        <Col xs={24} md={12}>
          <Card
            size="small"
            title={
              <Text strong style={{ fontSize: 12, color: '#020D7F', textTransform: 'uppercase', letterSpacing: 0.5 }}>
                <FileTextOutlined /> Movimentações recentes
              </Text>
            }
          >
            {dashboard.movimentacoes_recentes.length === 0 ? (
              <Empty description="Nenhuma movimentação registrada." />
            ) : (
              <Space direction="vertical" size={4} style={{ width: '100%' }}>
                {dashboard.movimentacoes_recentes.map(mv => (
                  <div key={mv.id} style={{
                    padding: 6, borderRadius: 4, background: '#FAFAFA',
                    borderLeft: '3px solid ' + (mv.tipo === 'entrada' ? '#0a7f4a' : mv.tipo === 'saida' ? '#B07500' : '#0930D0'),
                  }}>
                    <Text strong style={{ fontSize: 11 }}>
                      {mv.tipo === 'entrada' ? '+' : mv.tipo === 'saida' ? '−' : '~'}
                      {' '}{fmtNum(mv.quantidade)} {mv.material_unidade} · {mv.material_nome}
                    </Text>
                    <div>
                      <Text type="secondary" style={{ fontSize: 10 }}>
                        {fmtDataBr(mv.data_movimentacao)} · {mv.motivo || mv.tipo}
                        {mv.contrato_numero && ` · ${mv.contrato_numero}`}
                      </Text>
                    </div>
                  </div>
                ))}
              </Space>
            )}
          </Card>
        </Col>
      </Row>
    </>
  );
}

function ListaMovimentacoes({ movimentacoes, setMovimentacoes }: {
  movimentacoes: Movimentacao[];
  setMovimentacoes: (mvs: Movimentacao[]) => void;
}) {
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    (async () => {
      try {
        const token = await getToken();
        const r = await axios.post(`${API_URL}/comercial/propostas-geradas`,
          { action: 'admin_dashboard_estoque' },
          { headers: { Authorization: token } });
        const d = r.data.body ? JSON.parse(r.data.body) : r.data;
        setMovimentacoes(d.movimentacoes_recentes || []);
      } finally { setLoading(false); }
    })();
    // eslint-disable-next-line
  }, []);

  const cols: ColumnsType<Movimentacao> = [
    { title: 'Data', dataIndex: 'data_movimentacao', key: 'd', width: 100, render: (v) => fmtDataBr(v) },
    {
      title: 'Tipo', dataIndex: 'tipo', key: 't', width: 90,
      render: (v) => (
        <Tag color={v === 'entrada' ? 'green' : v === 'saida' ? 'gold' : 'blue'}>{v}</Tag>
      ),
    },
    { title: 'Material', dataIndex: 'material_nome', key: 'm' },
    {
      title: 'Qtd', dataIndex: 'quantidade', key: 'q', align: 'right', width: 110,
      render: (_, mv) => `${fmtNum(mv.quantidade)} ${mv.material_unidade}`,
    },
    {
      title: 'Custo', dataIndex: 'custo_total', key: 'ct', align: 'right', width: 120,
      render: (v) => v != null ? fmtBRL(v) : '—',
    },
    { title: 'Motivo', dataIndex: 'motivo', key: 'mt', render: (v) => v || '—' },
    {
      title: 'Contrato', dataIndex: 'contrato_numero', key: 'cn', width: 120,
      render: (v) => v ? <Text style={{ fontFamily: 'monospace', color: '#0930D0', fontSize: 11 }}>{v}</Text> : '—',
    },
    {
      title: 'Por', dataIndex: 'criado_por', key: 'p', width: 120,
      render: (v) => <Text type="secondary" style={{ fontSize: 10 }}>{v?.split('@')[0] || '—'}</Text>,
    },
  ];

  return (
    <Table
      rowKey="id"
      columns={cols}
      dataSource={movimentacoes}
      loading={loading}
      size="small"
      pagination={{ pageSize: 50 }}
      locale={{ emptyText: <Empty description="Nenhuma movimentação registrada ainda" /> }}
      style={{ marginTop: 14 }}
    />
  );
}

function ModalMaterial({ material, categorias, onFechar, onSalvo }: {
  material: Material | null;
  categorias: string[];
  onFechar: () => void;
  onSalvo: () => void;
}) {
  const { message } = AntApp.useApp();
  const [nome, setNome] = useState(material?.nome || '');
  const [categoria, setCategoria] = useState(material?.categoria || '');
  const [descricao, setDescricao] = useState(material?.descricao || '');
  const [unidade, setUnidade] = useState(material?.unidade || 'un');
  const [custoUnit, setCustoUnit] = useState<number>(Number(material?.custo_unitario) || 0);
  const [estoqueAtual, setEstoqueAtual] = useState<number>(Number(material?.estoque_atual) || 0);
  const [estoqueMin, setEstoqueMin] = useState<number>(Number(material?.estoque_minimo) || 0);
  const [fornecedor, setFornecedor] = useState(material?.fornecedor_padrao || '');
  const [observacoes, setObservacoes] = useState(material?.observacoes || '');
  const [salvando, setSalvando] = useState(false);

  async function salvar() {
    if (!nome.trim()) { message.warning('Nome é obrigatório'); return; }
    setSalvando(true);
    try {
      const token = await getToken();
      const action = material ? 'admin_atualizar_material' : 'admin_criar_material';
      const payload: any = {
        nome: nome.trim(),
        categoria: categoria.trim() || null,
        descricao: descricao.trim() || null,
        unidade: unidade.trim() || 'un',
        custo_unitario: custoUnit || 0,
        estoque_minimo: estoqueMin || 0,
        fornecedor_padrao: fornecedor.trim() || null,
        observacoes: observacoes.trim() || null,
      };
      if (!material) payload.estoque_atual = estoqueAtual || 0;
      const body = material
        ? { action, id: material.id, ...payload }
        : { action, ...payload };
      await axios.post(`${API_URL}/comercial/propostas-geradas`, body,
        { headers: { Authorization: token } });
      onSalvo();
    } catch (e: any) {
      message.error('Erro: ' + (e.response?.data?.error || e.message));
    } finally { setSalvando(false); }
  }

  const catOpts = [...categorias, 'campo', 'epi', 'uniforme', 'escritorio', 'consumivel']
    .filter((c, i, arr) => arr.indexOf(c) === i)
    .map(c => ({ value: c }));

  return (
    <Modal
      open onCancel={onFechar} onOk={salvar} confirmLoading={salvando}
      title={material ? 'Editar material' : 'Novo material'}
      okText={material ? 'Salvar' : 'Cadastrar'} cancelText="Cancelar"
      width={620}
      destroyOnClose
    >
      <Form layout="vertical" size="small">
        <Form.Item label="Nome" required>
          <Input value={nome} onChange={e => setNome(e.target.value)} placeholder="Ex: Marco geodésico de alumínio" autoFocus />
        </Form.Item>
        <Row gutter={10}>
          <Col span={16}>
            <Form.Item label="Categoria">
              <AutoComplete
                value={categoria} onChange={(v) => setCategoria(v)}
                options={catOpts}
                placeholder="campo, epi, uniforme, escritorio…"
              />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item label="Unidade">
              <Input value={unidade} onChange={e => setUnidade(e.target.value)} placeholder="un, par, cx, kg, m…" />
            </Form.Item>
          </Col>
        </Row>
        <Form.Item label="Descrição">
          <Input.TextArea value={descricao} onChange={e => setDescricao(e.target.value)} rows={2}
            placeholder="Detalhes técnicos, especificação, marca…" />
        </Form.Item>
        <Row gutter={10}>
          <Col span={!material ? 8 : 12}>
            <Form.Item label="Custo unitário (R$)">
              <InputNumber value={custoUnit} onChange={(v) => setCustoUnit(Number(v) || 0)}
                min={0} step={0.01} style={{ width: '100%' }} />
            </Form.Item>
          </Col>
          {!material && (
            <Col span={8}>
              <Form.Item label="Estoque inicial">
                <InputNumber value={estoqueAtual} onChange={(v) => setEstoqueAtual(Number(v) || 0)}
                  min={0} step={0.01} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
          )}
          <Col span={!material ? 8 : 12}>
            <Form.Item label="Estoque mínimo (alerta)">
              <InputNumber value={estoqueMin} onChange={(v) => setEstoqueMin(Number(v) || 0)}
                min={0} step={0.01} style={{ width: '100%' }} />
            </Form.Item>
          </Col>
        </Row>
        <Form.Item label="Fornecedor padrão">
          <Input value={fornecedor} onChange={e => setFornecedor(e.target.value)} placeholder="Empresa preferida pra reposição" />
        </Form.Item>
        <Form.Item label="Observações">
          <Input.TextArea value={observacoes} onChange={e => setObservacoes(e.target.value)} rows={2}
            placeholder="Notas internas, instruções de uso…" />
        </Form.Item>
      </Form>
    </Modal>
  );
}

function ModalMovimentacao({ material, tipoInicial, onFechar, onSalvo }: {
  material: Material;
  tipoInicial: 'entrada' | 'saida' | 'ajuste';
  onFechar: () => void;
  onSalvo: () => void;
}) {
  const { message } = AntApp.useApp();
  const [tipo, setTipo] = useState(tipoInicial);
  const [quantidade, setQuantidade] = useState<number>(0);
  const [custoUnit, setCustoUnit] = useState<number>(Number(material.custo_unitario) || 0);
  const [motivo, setMotivo] = useState(tipoInicial === 'entrada' ? 'compra' : 'uso_projeto');
  const [observacoes, setObservacoes] = useState('');
  const [salvando, setSalvando] = useState(false);

  async function salvar() {
    if (!(quantidade > 0)) { message.warning('Quantidade deve ser maior que zero'); return; }
    setSalvando(true);
    try {
      const token = await getToken();
      await axios.post(`${API_URL}/comercial/propostas-geradas`,
        { action: 'admin_registrar_movimentacao',
          material_id: material.id, tipo, quantidade,
          custo_unitario: tipo === 'entrada' ? custoUnit : null,
          motivo: motivo || null,
          observacoes: observacoes.trim() || null },
        { headers: { Authorization: token } });
      onSalvo();
    } catch (e: any) {
      message.error('Erro: ' + (e.response?.data?.error || e.message));
    } finally { setSalvando(false); }
  }

  const motivoOpts = [
    { value: 'compra' }, { value: 'uso_projeto' }, { value: 'descarte' },
    { value: 'inventario' }, { value: 'perda' }, { value: 'emprestimo' },
  ];

  return (
    <Modal
      open onCancel={onFechar} onOk={salvar} confirmLoading={salvando}
      title={`📝 ${material.nome}`} okText="Registrar" cancelText="Cancelar"
      width={520} destroyOnClose
    >
      <Alert
        type="info"
        message={
          <Text style={{ fontSize: 11 }}>
            Estoque atual: <strong>{fmtNum(material.estoque_atual)} {material.unidade}</strong>
            {' '}· Mínimo: {fmtNum(material.estoque_minimo)}
          </Text>
        }
        style={{ marginBottom: 12 }}
      />
      <Form layout="vertical" size="small">
        <Form.Item label="Tipo de movimentação">
          <Radio.Group value={tipo} onChange={(e) => setTipo(e.target.value)} optionType="button" buttonStyle="solid">
            <Radio.Button value="entrada">+ Entrada</Radio.Button>
            <Radio.Button value="saida">− Saída</Radio.Button>
            <Radio.Button value="ajuste">~ Ajuste</Radio.Button>
          </Radio.Group>
        </Form.Item>
        <Form.Item label={tipo === 'ajuste' ? `Novo estoque (${material.unidade})` : `Quantidade (${material.unidade})`}>
          <InputNumber value={quantidade} onChange={(v) => setQuantidade(Number(v) || 0)}
            min={0.01} step={0.01} style={{ width: '100%' }} autoFocus />
        </Form.Item>
        {tipo === 'entrada' && (
          <Form.Item label="Custo unitário (R$)">
            <InputNumber value={custoUnit} onChange={(v) => setCustoUnit(Number(v) || 0)}
              min={0} step={0.01} style={{ width: '100%' }} />
          </Form.Item>
        )}
        <Form.Item label="Motivo">
          <AutoComplete value={motivo} onChange={(v) => setMotivo(v)} options={motivoOpts}
            placeholder={tipo === 'entrada' ? 'compra, doação…' : 'uso_projeto, descarte, perda…'} />
        </Form.Item>
        <Form.Item label="Observações">
          <Input.TextArea value={observacoes} onChange={e => setObservacoes(e.target.value)} rows={2}
            placeholder="Detalhes adicionais…" />
        </Form.Item>
      </Form>
    </Modal>
  );
}

export default function Estoque() {
  return (
    <AntApp>
      <EstoqueInner />
    </AntApp>
  );
}
